import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { useAuth } from './AuthContext';
import { api } from '../api/http';
import RecoveryCodes from './RecoveryCodes';
import PasswordRevealInput from '../components/PasswordRevealInput';

type Phase = 'credentials' | 'set_password' | 'enroll_2fa' | 'totp' | 'recovery_codes';

interface NativeLoginFormProps {
  onForgotPassword?: () => void;
}

export default function NativeLoginForm({ onForgotPassword }: NativeLoginFormProps = {}) {
  const { markNativeAuthenticated } = useAuth();

  const [phase, setPhase] = useState<Phase>('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [secret, setSecret] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Credentials phase
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  // Set-password phase
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // QR code data URL for enroll_2fa phase
  const [qrDataUrl, setQrDataUrl] = useState('');

  // TOTP / enroll code
  const [code, setCode] = useState('');

  // Recovery codes shown after enrollment
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  // Toggle for recovery-code entry at the totp step
  const [useRecovery, setUseRecovery] = useState(false);

  function navigateHome() {
    window.location.assign('/');
  }

  function handleSuccess() {
    markNativeAuthenticated();
    navigateHome();
  }

  async function fetchEnrollData(token: string) {
    try {
      const res = await api.get<{ provisioning_uri: string; secret: string; pending_token: string }>(
        `/auth/native/enroll-2fa?pending_token=${encodeURIComponent(token)}`
      );
      setProvisioningUri(res.data.provisioning_uri);
      setSecret(res.data.secret);
      // Backend may return a refreshed token
      if (res.data.pending_token) setPendingToken(res.data.pending_token);
    } catch {
      setError('Failed to load authenticator setup. Please try again.');
    }
  }

  // When we enter enroll_2fa phase, load provisioning data if not already loaded
  useEffect(() => {
    if (phase === 'enroll_2fa' && pendingToken && !provisioningUri) {
      fetchEnrollData(pendingToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Generate QR data URL whenever provisioningUri becomes available
  useEffect(() => {
    if (provisioningUri) {
      QRCode.toDataURL(provisioningUri, { margin: 1, width: 196 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(''));
    }
  }, [provisioningUri]);

  useEffect(() => {
    if (!secretCopied) return;
    const timer = window.setTimeout(() => setSecretCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [secretCopied]);

  async function copySecret() {
    if (!secret) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setError(null);
    } catch {
      setError('Could not copy the setup key. Press and hold the key to copy it manually.');
    }
  }

  async function handleCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ status: string; pending_token?: string }>(
        '/auth/native/login',
        { identifier, password, remember }
      );
      const { status, pending_token } = res.data;
      if (pending_token) setPendingToken(pending_token);

      if (status === 'ok') {
        handleSuccess();
      } else if (status === 'must_change_password') {
        setPhase('set_password');
      } else if (status === 'enroll_2fa') {
        setPhase('enroll_2fa');
        if (pending_token) await fetchEnrollData(pending_token);
      } else if (status === 'totp_required') {
        setPhase('totp');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr?.response?.status;
      if (status === 401) {
        setError('Invalid email or password.');
      } else if (status === 429) {
        setError('Too many attempts, try again later.');
      } else if (status === 423) {
        setError('Account temporarily locked. Please contact your administrator.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ status: string; pending_token?: string }>(
        '/auth/native/set-password',
        { pending_token: pendingToken, new_password: newPassword }
      );
      const { status, pending_token } = res.data;
      if (pending_token) setPendingToken(pending_token);

      if (status === 'ok') {
        handleSuccess();
      } else if (status === 'enroll_2fa') {
        setPhase('enroll_2fa');
        if (pending_token) await fetchEnrollData(pending_token);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const status = axiosErr?.response?.status;
      if (status === 422) {
        const detail = axiosErr?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Password does not meet requirements.');
      } else {
        setError('Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleEnroll(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ status: string; recovery_codes?: string[] }>(
        '/auth/native/enroll-2fa',
        { pending_token: pendingToken, code }
      );
      if (res.data.recovery_codes?.length) {
        setRecoveryCodes(res.data.recovery_codes);
        setPhase('recovery_codes');
      } else {
        handleSuccess();
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr?.response?.status;
      if (status === 401) {
        setError('Invalid code. Please check your authenticator and try again.');
      } else {
        setError('Enrollment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/native/totp', { pending_token: pendingToken, code });
      handleSuccess();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr?.response?.status;
      if (status === 401) {
        setError('Invalid code.');
      } else if (status === 423) {
        setError('Account locked. Please contact your administrator.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.65rem 0.85rem',
    fontSize: '0.9rem',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--input, #fff)',
    color: 'var(--text)',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: '0.35rem',
    textAlign: 'left',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const submitBtnStyle = (disabled: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '0.75rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    background: disabled ? '#78716c' : 'var(--primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s ease',
    marginTop: '0.25rem',
  });

  if (phase === 'credentials') {
    return (
      <form onSubmit={handleCredentials} noValidate>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="native-identifier">Email</label>
          <input
            id="native-identifier"
            type="email"
            autoComplete="username email"
            required
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            style={inputStyle}
            placeholder="you@firm.com"
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="native-password">Password</label>
          <PasswordRevealInput
            id="native-password"
            autoComplete="current-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyUp={e => setCapsOn(e.getModifierState && e.getModifierState('CapsLock'))}
            inputStyle={inputStyle}
            placeholder="Password"
          />
          {capsOn && (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.3rem 0 0', textAlign: 'left' }}>
              ⇪ Caps Lock is on
            </p>
          )}
        </div>
        {error && (
          <p style={{ fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.75rem', textAlign: 'left' }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <input
            id="native-remember"
            type="checkbox"
            checked={remember}
            onChange={e => setRemember(e.target.checked)}
            style={{ cursor: 'pointer', margin: 0 }}
          />
          <label
            htmlFor="native-remember"
            style={{ fontSize: '0.82rem', color: 'var(--text-muted)', cursor: 'pointer', margin: 0 }}
          >
            Stay signed in for a week
          </label>
        </div>
        <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
        {onForgotPassword && (
          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={onForgotPassword}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: '0.8rem',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Forgot password?
            </button>
          </div>
        )}
      </form>
    );
  }

  if (phase === 'set_password') {
    return (
      <form onSubmit={handleSetPassword} noValidate>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.25rem', textAlign: 'left' }}>
          You must set a new password before continuing.
        </p>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="native-new-password">New Password</label>
          <PasswordRevealInput
            id="native-new-password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            inputStyle={inputStyle}
            placeholder="New password"
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.35rem 0 0', textAlign: 'left' }}>
            At least 12 characters. Avoid common passwords.
          </p>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="native-confirm-password">Confirm Password</label>
          <PasswordRevealInput
            id="native-confirm-password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            inputStyle={inputStyle}
            placeholder="Confirm new password"
          />
        </div>
        {error && (
          <p style={{ fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.75rem', textAlign: 'left' }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>
          {loading ? 'Setting password...' : 'Set Password'}
        </button>
      </form>
    );
  }

  if (phase === 'recovery_codes') {
    return (
      <div>
        <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.75rem', textAlign: 'left' }}>
          Two-factor authentication enabled!
        </p>
        <RecoveryCodes codes={recoveryCodes} />
        <button
          type="button"
          onClick={handleSuccess}
          style={{ ...submitBtnStyle(false), marginTop: '1.25rem' }}
        >
          Continue to Valentine
        </button>
      </div>
    );
  }

  if (phase === 'enroll_2fa') {
    return (
      <form onSubmit={handleEnroll} noValidate>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem', textAlign: 'left' }}>
          Set up two-factor authentication with an authenticator app. On this phone, open the setup link or copy the key.
        </p>
        {secret ? (
          <div style={{
            background: 'var(--bg, #fefcf9)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: '0.85rem',
            marginBottom: '1.25rem',
            textAlign: 'left',
          }}>
            {provisioningUri && (
              <a
                href={provisioningUri}
                data-testid="totp-open-authenticator"
                style={{
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0.72rem 1rem',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--primary)',
                  color: '#fff',
                  textAlign: 'center',
                  textDecoration: 'none',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  marginBottom: '0.85rem',
                }}
              >
                Open authenticator app
              </a>
            )}
            {qrDataUrl && (
              <details style={{ marginBottom: '0.85rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                  Scan QR from another device
                </summary>
                <img
                  src={qrDataUrl}
                  alt="Authenticator QR code"
                  width={196}
                  height={196}
                  style={{ display: 'block', margin: '0.75rem auto 0', background: '#fff', borderRadius: 12, padding: 8 }}
                />
              </details>
            )}
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.4rem', fontWeight: 600 }}>
              Setup key
            </p>
            <p
              data-testid="totp-secret"
              style={{
                fontSize: '0.85rem',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                color: 'var(--text)',
                margin: '0 0 0.5rem',
                wordBreak: 'break-all',
                userSelect: 'text',
              }}
            >
              {secret}
            </p>
            <button
              type="button"
              onClick={copySecret}
              data-testid="totp-copy-secret"
              style={{
                width: '100%',
                padding: '0.62rem 0.85rem',
                fontSize: '0.82rem',
                fontWeight: 700,
                color: 'var(--text)',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                marginBottom: provisioningUri ? '0.75rem' : 0,
              }}
            >
              {secretCopied ? 'Copied setup key' : 'Copy setup key'}
            </button>
            {provisioningUri && (
              <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                  Show setup URI
                </summary>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.5rem 0 0', wordBreak: 'break-all' }}>
                  <span style={{ userSelect: 'text' }}>{provisioningUri}</span>
                </p>
              </details>
            )}
          </div>
        ) : (
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Loading authenticator setup...
          </p>
        )}
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="native-enroll-code">6-digit code from your app</label>
          <input
            id="native-enroll-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            style={{ ...inputStyle, letterSpacing: '0.2em', textAlign: 'center' }}
            placeholder="000000"
          />
        </div>
        {error && (
          <p style={{ fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.75rem', textAlign: 'left' }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={loading || !secret} style={submitBtnStyle(loading || !secret)}>
          {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
        </button>
      </form>
    );
  }

  // phase === 'totp'
  return (
    <form onSubmit={handleTotp} noValidate>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.25rem', textAlign: 'left' }}>
        {useRecovery
          ? 'Enter one of your saved recovery codes.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="native-totp-code">
          {useRecovery ? 'Recovery code' : 'Authentication Code'}
        </label>
        {useRecovery ? (
          <input
            id="native-totp-code"
            type="text"
            autoComplete="off"
            required
            value={code}
            onChange={e => setCode(e.target.value)}
            style={{ ...inputStyle, letterSpacing: '0.05em', textAlign: 'left' }}
            placeholder="xxxxx-xxxxx-xxxxx-xxxxx"
          />
        ) : (
          <input
            id="native-totp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            style={{ ...inputStyle, letterSpacing: '0.2em', textAlign: 'center' }}
            placeholder="000000"
          />
        )}
      </div>
      {error && (
        <p style={{ fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.75rem', textAlign: 'left' }}>
          {error}
        </p>
      )}
      <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>
        {loading ? 'Verifying...' : 'Verify'}
      </button>
      <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
        <button
          type="button"
          onClick={() => { setUseRecovery(v => !v); setCode(''); setError(null); }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {useRecovery ? 'Use authenticator app instead' : 'Use a recovery code instead'}
        </button>
      </div>
    </form>
  );
}
